import React, {useEffect, useState} from 'react';
import {Button, Grid, Header, Icon, Input, Label, Pagination, Segment, Table} from 'semantic-ui-react';
import {useNavigate} from "react-router-dom";
import moment from "moment/moment";
import {toast} from "react-toastify";
import {SessionUser} from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import {MotoristaFilter} from "../../../filters/motorista-filter";
import {useBlockUI} from "../../../shared/services/block-ui-service";
import {Gestor} from "../../../models/gestor";
import {ToasterConfig} from "../../../config/ToasterConfig";
import useManagerService from "../../../services/backoffice/hooks/useManagerService";
import TooltipInfo from "../../../components/tooltip/tooltip";

const ManagersListPage = () => {
    const [managers, setManagers] = useState<Gestor[]>([]);
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const [filter, setFilter] = useState<MotoristaFilter>()
    const [totalPages, setTotalPages] = useState<number>()
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const blockUI = useBlockUI();
    const {getManagers, activateManager, deactiveManager, deleteManager, resetManagerPassword} = useManagerService();
    const profile = sessionUser?.userData?.profile;
    const isAdmin = profile === 'Admin';
    const isManager = profile === 'Manager';

    useEffect(() => {
        blockUI?.setActive(loading)
    }, [loading]);

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user)
        }

        updateSessionUser()
    }, [])

    const [showFilters, setShowFilters] = useState(false);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        if (!profile) return;
        if (isAdmin || isManager) {
            listManagers();
        }
    }, [filter?.Page, profile]);

    async function listManagers() {
        setLoading(true)
        const response = (await getManagers(filter).finally(() => setLoading(false)));
        setTotalPages(response.totalPages);
        setManagers(response.itens);
    }

    const toggleActivationManager = async (gestor: Gestor) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${gestor.isActive ? 'desativar' : 'ativar'} o gestor ${gestor.firstName} ${gestor.lastName}?`);
            if (!result) return
            setLoading(true);
            const activationResult = !gestor.isActive ? await activateManager(gestor.id) : await deactiveManager(gestor.id);
            if (!!activationResult) {
                toast.success(`O Gestor foi ${gestor.isActive ? 'desativado' : 'ativado'} com sucesso`, ToasterConfig.success);
                listManagers()
            }
            setLoading(false);
        } catch (error: any) {
            toast.warn(`Falha ao ${gestor.isActive ? 'desativar' : 'ativar'} gestor`, ToasterConfig.error);
            setLoading(false);
            setError(error.message);
        }
    };

    async function deleteManagersFunction(gestor: Gestor) {
        try {
            const confirm = window.confirm(`Tem certeza que deseja apagar o gestor ?`);

            if (confirm) {
                await deleteManager(gestor.id);
                toast.success('Gestor deletado com sucesso', ToasterConfig.success);
                listManagers()
            }
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }

    const handleCopyPassword = (password: string) => {
        navigator.clipboard.writeText(password);
        toast.info('Senha copiada para a área de transferência');
    };

    async function resetManagerPasswordFunction(manager: Gestor) {
        const confirm = window.confirm(
            `Redefinir a senha de ${manager.firstName} para os 6 primeiros dígitos do CPF?`
        );
        if (!confirm) return;

        try {
            setLoading(true);
            const result = await resetManagerPassword(manager.id);
            toast.success(
                <div>
                    {result.message}
                    {result.password && (
                        <button type="button" onClick={() => handleCopyPassword(result.password!)}>
                            Copiar senha
                        </button>
                    )}
                </div>,
                {autoClose: false}
            );
        } catch (error: any) {
            const message = error?.response?.data?.errors?.[0]
                ?? error?.response?.data?.message
                ?? 'Falha ao redefinir senha do gestor';
            toast.warn(message, ToasterConfig.error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div>
            <Header as="h3" textAlign="center" color={'green'}>
                Gestores
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {isAdmin &&
                        <Button color="green"
                                onClick={() => navigate('/backoffice/managers/create/')}>

                            <Icon name='plus'/> Novo
                        </Button>}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color={"green"}>
                        <Icon name={showFilters ? 'hide' : 'unhide'}/>
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>
            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={8}>
                                <label htmlFor="managerName">Nome do gestor</label>
                                <Input
                                    fluid
                                    id="managerName"
                                    icon="search"
                                    type="text"
                                    placeholder="Nome do gestor"
                                    value={filter?.Name ?? ''}
                                    onChange={event => setFilter(prev => ({...prev, Name: event.target?.value}))}
                                />
                            </Grid.Column>
                            <Grid.Column width={6}>
                                <label htmlFor="email">Email</label>
                                <Input
                                    fluid
                                    id="email"
                                    icon="search"
                                    type="text"
                                    placeholder="Email"
                                    value={filter?.Email ?? ''}
                                    onChange={event => setFilter(prev => ({...prev, Email: event.target?.value}))}
                                />
                            </Grid.Column>
                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color={"green"} onClick={listManagers}>Filtrar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}


            <Table color={"green"}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>Email</Table.HeaderCell>
                        <Table.HeaderCell>Cliente</Table.HeaderCell>
                        <Table.HeaderCell>Criado em</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                {(isAdmin || isManager) &&
                    <Table.Body>
                        {managers?.map((manager) => (
                            <Table.Row key={manager.id}>
                                <Table.Cell>
                                    <Button.Group>
                                        {(isAdmin || isManager) && (
                                            <TooltipInfo
                                                content="Editar / alterar senha"
                                                trigger={
                                                    <Button
                                                        color="blue"
                                                        onClick={() => {
                                                            setLoading(true);
                                                            navigate('/backoffice/managers/edit/' + manager.id);
                                                        }}
                                                        icon="edit"
                                                    />
                                                }
                                            />
                                        )}

                                        {isAdmin && (
                                            <>
                                                <TooltipInfo
                                                    content="Excluir gestor"
                                                    trigger={
                                                        <Button
                                                            color="red"
                                                            onClick={() => deleteManagersFunction(manager)}
                                                            icon="trash"
                                                        />
                                                    }
                                                />

                                                <TooltipInfo
                                                    content={manager.isActive ? "Desativar gestor" : "Ativar gestor"}
                                                    trigger={
                                                        <Button
                                                            color={manager.isActive ? 'red' : 'green'}
                                                            onClick={() => toggleActivationManager(manager)}
                                                            icon={manager.isActive ? 'power off' : 'power'}
                                                        />
                                                    }
                                                />
                                            </>
                                        )}

                                        {(isAdmin || isManager) && (
                                            <TooltipInfo
                                                content="Redefinir senha (6 primeiros dígitos do CPF)"
                                                trigger={
                                                    <Button
                                                        color="orange"
                                                        onClick={() => resetManagerPasswordFunction(manager)}
                                                        icon="key"
                                                    />
                                                }
                                            />
                                        )}
                                    </Button.Group>

                                </Table.Cell>
                                <Table.Cell>{`${manager.firstName} ${manager?.lastName ?? ''}`}</Table.Cell>
                                <Table.Cell>{manager.email}</Table.Cell>
                                <Table.Cell>{`${manager?.clientName ?? '-'}`}</Table.Cell>
                                <Table.Cell>{moment(manager.createdAt).format('DD/MM/YY HH:mm')}</Table.Cell>
                                <Table.Cell>
                                    <Label key={manager.id}
                                           color={!manager.isActive ? 'red' : 'green'}>{manager.isActive ? 'Sim' : 'Não'}</Label>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>}
            </Table>
            <Pagination totalPages={totalPages ?? 0}
                        defaultActivePage={filter?.Page ?? 1}
                        onPageChange={(e, {activePage}) => {
                            setFilter(prev => ({...prev, Page: activePage as number}))
                        }
                        }>
            </Pagination>
        </div>
    );
};

export default ManagersListPage;
